import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileRejection, useDropzone } from "react-dropzone";
import {
  Alert,
  Box,
  Checkbox,
  FormHelperText,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CloseRounded,
  CloudUploadOutlined,
  ErrorOutlineRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { getFileTypeStyle } from "../../../Helpers/fileTypePresentation";
import { documentCraftingFileUrls } from "../../../Api/documentCrafting";
import {
  DocumentCraftingFormField,
  DocumentCraftingImageMeta,
  DocumentCraftingUpload,
} from "../../../Models/documentCrafting";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import AuthImage from "../Common/AuthImage";
import { formatFileSize } from "../Common/format";
import { LocalImageInfo, PICTURE_FILE_KINDS, PendingFile, altProblem } from "./documentFormModel";
import {
  COMO_NAVY,
  acceptedExtensions,
  dropzoneAccept,
  extensionList,
  isImageFileName,
  logoWarning,
  matchesAccept,
  maxFileMb,
  readImageInfo,
} from "./fileRules";

interface Props {
  field: DocumentCraftingFormField;
  /** DOM id of the drop area (focus target of `?field=`). */
  id?: string;
  label: string;
  required: boolean;
  help?: string;
  files: PendingFile[];
  onFilesChange: (update: (current: PendingFile[]) => PendingFile[]) => void;
  /** Earlier uploads of this field (duplicate source or base revision). */
  existing?: DocumentCraftingUpload[];
  /** Document the earlier uploads belong to (thumbnails). */
  existingDocumentId?: number | null;
  selectedIds?: number[];
  onSelectedIdsChange?: (ids: number[]) => void;
  /** Workspace limit per file (`max_upload_mb`). */
  workspaceMaxMb?: number | null;
  /** Edited alt texts of earlier uploads (revise); unset: not editable. */
  existingAlts?: Record<number, string>;
  onExistingAltChange?: (uploadId: number, alt: string) => void;
  error?: string | null;
  disabled?: boolean;
}

const sameFile = (a: File, b: File) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

const pendingId = (file: File) =>
  `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 10)}`;

type ImageFacts = Pick<
  DocumentCraftingImageMeta,
  "width_px" | "height_px" | "has_alpha" | "mean_luminance"
>;

/** A picked image as an object URL, revoked when the row goes away. */
const LocalImage: React.FC<{ file: File; navy?: boolean }> = ({ file, navy }) => {
  const [src, setSrc] = useState<{ file: File; url: string } | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc({ file, url });
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const url = src?.file === file ? src.url : null;
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: navy ? COMO_NAVY : color.gray100,
      }}
    >
      {url && (
        <Box
          component="img"
          src={url}
          alt=""
          sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      )}
    </Box>
  );
};

const Thumb: React.FC<{ name: string; children?: React.ReactNode; wide?: boolean }> = ({
  name,
  children,
  wide,
}) => {
  const { Icon, iconColor, iconBg } = getFileTypeStyle(name);
  return (
    <Box
      sx={{
        width: wide ? 120 : 44,
        height: 44,
        flexShrink: 0,
        borderRadius: "8px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: iconBg,
        color: iconColor,
      }}
    >
      {children ?? <Icon sx={{ fontSize: 22 }} />}
    </Box>
  );
};

/** Dimensions plus the warnings of one image (too small, dark logo). */
const ImageNotes: React.FC<{
  image: ImageFacts | LocalImageInfo | null | undefined;
  minWidth?: number | null;
  logo?: boolean;
  reading?: boolean;
}> = ({ image, minWidth, logo, reading }) => {
  const { t } = useTranslation("documentCrafting");
  if (reading) {
    return (
      <Typography sx={{ fontSize: 12, color: color.gray500 }}>
        {t("create.upload.reading")}
      </Typography>
    );
  }
  if (!image) return null;
  // Too narrow is an error (the server rejects the image); the logo checks
  // are warnings.
  const tooSmall = !!minWidth && image.width_px < minWidth;
  const warnings: string[] = [];
  const logoIssue = logo ? logoWarning(image) : null;
  if (logoIssue === "dark") warnings.push(t("create.upload.logoDark"));
  if (logoIssue === "opaque") warnings.push(t("create.upload.logoOpaque"));
  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: color.gray500 }}>
        {t("create.upload.dimensions", {
          width: image.width_px,
          height: image.height_px,
        })}
      </Typography>
      {tooSmall && (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-start", mt: 0.25 }}>
          <ErrorOutlineRounded sx={{ fontSize: 15, color: color.red600, mt: "1px" }} />
          <Typography sx={{ fontSize: 12, color: color.red600 }}>
            {t("create.upload.tooSmall", { width: image.width_px, min: minWidth })}
          </Typography>
        </Box>
      )}
      {warnings.map((warning) => (
        <Box
          key={warning}
          sx={{ display: "flex", gap: 0.5, alignItems: "flex-start", mt: 0.25 }}
        >
          <WarningAmberRounded sx={{ fontSize: 15, color: color.orange600, mt: "1px" }} />
          <Typography sx={{ fontSize: 12, color: color.orange600 }}>{warning}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const rowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  p: 1,
  borderRadius: "10px",
  border: `1px solid ${color.gray200}`,
  backgroundColor: color.white,
};

/**
 * File input of the schema form: a drop area with the limits of the field
 * (types, size, count), inline errors instead of alerts, earlier uploads that
 * can be unticked, image dimensions with a warning below `min_width_px`, and
 * partner logos previewed on the navy band with a dark-logo warning.
 */
const UploadField: React.FC<Props> = ({
  field,
  id,
  label,
  required,
  help,
  files,
  onFilesChange,
  existing = [],
  existingDocumentId,
  selectedIds = [],
  onSelectedIdsChange,
  workspaceMaxMb,
  existingAlts,
  onExistingAltChange,
  error,
  disabled,
}) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const pictures = PICTURE_FILE_KINDS.includes(field.file_kind ?? "");
  const setAlt = (fileId: string, alt: string) =>
    onFilesChange((current) => current.map((item) => (item.id === fileId ? { ...item, alt } : item)));
  const altInput = (inputId: string, value: string, onChange: (alt: string) => void) => {
    const problem = altProblem(value);
    return (
      <TextField
        id={inputId}
        size="small"
        fullWidth
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        label={t("create.upload.altLabel")}
        error={!!problem}
        helperText={problem ? t(`create.upload.${problem}`) : t("create.upload.altHelp")}
        inputProps={{ maxLength: 250 }}
        sx={{ mt: 1 }}
      />
    );
  };
  const { accent, accentSoft } = useDashboardColors();
  const [rejections, setRejections] = useState<string[]>([]);

  const extensions = acceptedExtensions(field);
  const accept = dropzoneAccept(extensions);
  const maxMb = maxFileMb(field, workspaceMaxMb);
  const maxFiles = field.max_files ?? null;
  const single = maxFiles === 1;
  const used = selectedIds.length + files.length;
  const full = !single && maxFiles !== null && used >= maxFiles;
  const navy = !!field.preview_on_navy;

  const onDrop = (accepted: File[], rejected: FileRejection[]) => {
    const messages = rejected.map(({ file, errors }) => {
      const code = errors[0]?.code;
      if (code === "file-too-large") return t("create.upload.rejected.tooLarge", { name: file.name, max: maxMb });
      if (code === "file-invalid-type")
        return t("create.upload.rejected.type", { name: file.name, types: extensionList(extensions) });
      if (code === "too-many-files") return t("create.upload.rejected.tooMany", { name: file.name, max: maxFiles });
      return t("create.upload.rejected.other", { name: file.name });
    });

    const fresh: File[] = [];
    accepted.forEach((file) => {
      if (files.some((item) => sameFile(item.file, file)) || fresh.some((item) => sameFile(item, file))) {
        messages.push(t("create.upload.rejected.duplicate", { name: file.name }));
      } else {
        fresh.push(file);
      }
    });

    let added: PendingFile[];
    if (single) {
      // One file only: a new one replaces what was there.
      added = fresh.slice(0, 1).map((file) => ({ id: pendingId(file), file }));
      fresh.slice(1).forEach((file) =>
        messages.push(t("create.upload.rejected.tooMany", { name: file.name, max: 1 })),
      );
      if (added.length) {
        onFilesChange(() => added);
        if (selectedIds.length) onSelectedIdsChange?.([]);
      }
    } else {
      const room = maxFiles === null ? fresh.length : Math.max(0, maxFiles - used);
      added = fresh.slice(0, room).map((file) => ({ id: pendingId(file), file }));
      fresh.slice(room).forEach((file) =>
        messages.push(t("create.upload.rejected.tooMany", { name: file.name, max: maxFiles })),
      );
      if (added.length) onFilesChange((current) => [...current, ...added]);
    }
    setRejections(messages);

    added
      .filter((item) => isImageFileName(item.file.name))
      .forEach((item) => {
        readImageInfo(item.file).then((image) =>
          onFilesChange((current) =>
            current.map((entry) => (entry.id === item.id ? { ...entry, image } : entry)),
          ),
        );
      });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize: maxMb ? maxMb * 1024 * 1024 : undefined,
    multiple: !single,
    disabled: disabled || full,
    validator: accept
      ? undefined
      : (file: File) =>
          matchesAccept(file.name, extensions)
            ? null
            : { code: "file-invalid-type", message: "" },
  });

  const limits = [
    extensions.length ? extensionList(extensions) : null,
    maxMb ? t("create.upload.maxSize", { size: maxMb }) : null,
    maxFiles && !single ? t("create.upload.maxFiles", { count: maxFiles }) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const toggleExisting = (uploadId: number, checked: boolean) => {
    if (!onSelectedIdsChange) return;
    if (!checked) {
      onSelectedIdsChange(selectedIds.filter((value) => value !== uploadId));
      return;
    }
    if (single) {
      // Ticking the earlier file again replaces a newly picked one.
      onSelectedIdsChange([uploadId]);
      if (files.length) onFilesChange(() => []);
      return;
    }
    onSelectedIdsChange([...selectedIds, uploadId]);
  };

  const removeFile = (pendingFileId: string) =>
    onFilesChange((current) => current.filter((item) => item.id !== pendingFileId));

  return (
    <Box>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: color.gray900 }}>
        {label}
        {required && (
          <Box component="span" sx={{ color: color.red600 }}>
            {" *"}
          </Box>
        )}
      </Typography>
      {help && (
        <Typography sx={{ fontSize: 12, color: color.gray500, mb: 0.5 }}>{help}</Typography>
      )}

      {existing.length > 0 && (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: color.gray500 }}>
            {t("create.upload.earlier")}
          </Typography>
          {existing.map((upload) => {
            const checked = selectedIds.includes(upload.id);
            const image = isImageFileName(upload.filename) || !!upload.image_meta;
            return (
              <Box key={upload.id} sx={{ ...rowSx, opacity: checked ? 1 : 0.65 }}>
                <Tooltip title={t("create.upload.useEarlier")} arrow>
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={disabled || !onSelectedIdsChange}
                    onChange={(event) => toggleExisting(upload.id, event.target.checked)}
                    inputProps={{ "aria-label": t("create.upload.useEarlierFile", { name: upload.filename }) }}
                    sx={{ p: 0.5 }}
                  />
                </Tooltip>
                <Thumb name={upload.filename} wide={navy && image}>
                  {image && existingDocumentId ? (
                    <AuthImage
                      url={documentCraftingFileUrls.upload(existingDocumentId, upload.id)}
                      alt={upload.filename}
                      sx={{
                        width: "100%",
                        height: "100%",
                        backgroundColor: navy ? COMO_NAVY : color.gray100,
                        p: navy ? 0.75 : 0,
                      }}
                    />
                  ) : undefined}
                </Thumb>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: color.gray900 }}>
                    {upload.filename}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: color.gray500 }}>
                    {formatFileSize(upload.size, i18n.language)}
                  </Typography>
                  <ImageNotes image={upload.image_meta} minWidth={field.min_width_px} logo={navy} />
                  {pictures &&
                    checked &&
                    onExistingAltChange &&
                    altInput(
                      `${id ?? field.key}-alt-${upload.id}`,
                      existingAlts?.[upload.id] ?? upload.image_meta?.alt ?? "",
                      (alt) => onExistingAltChange(upload.id, alt),
                    )}
                </Box>
              </Box>
            );
          })}
          <Typography sx={{ fontSize: 12, color: color.gray500 }}>
            {t("create.upload.earlierHint")}
          </Typography>
        </Box>
      )}

      {files.length > 0 && (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {files.map((item) => {
            const image = isImageFileName(item.file.name);
            return (
              <Box key={item.id} sx={rowSx}>
                <Thumb name={item.file.name} wide={navy && image}>
                  {image ? <LocalImage file={item.file} navy={navy} /> : undefined}
                </Thumb>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: color.gray900 }}>
                    {item.file.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: color.gray500 }}>
                    {formatFileSize(item.file.size, i18n.language)}
                  </Typography>
                  {image && (
                    <ImageNotes
                      image={item.image}
                      minWidth={field.min_width_px}
                      logo={navy}
                      reading={item.image === undefined}
                    />
                  )}
                  {pictures &&
                    image &&
                    altInput(`${id ?? field.key}-alt-${item.id}`, item.alt ?? "", (alt) => setAlt(item.id, alt))}
                </Box>
                <Tooltip title={t("create.upload.remove")} arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => removeFile(item.id)}
                      aria-label={t("create.upload.removeFile", { name: item.file.name })}
                    >
                      <CloseRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        {...getRootProps()}
        id={id}
        sx={{
          mt: 1,
          px: 2,
          py: files.length || selectedIds.length ? 1.5 : 2.5,
          borderRadius: "10px",
          border: `1.5px dashed ${error ? color.red600 : isDragActive ? accent : color.gray300}`,
          backgroundColor: isDragActive ? accentSoft : color.gray50,
          textAlign: "center",
          cursor: disabled || full ? "default" : "pointer",
          outline: "none",
          transition: "border-color 120ms, background-color 120ms",
          "&:focus-visible": { borderColor: accent },
          opacity: disabled || full ? 0.6 : 1,
        }}
      >
        <input {...getInputProps()} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
          <CloudUploadOutlined sx={{ color: color.gray400 }} />
          <Typography sx={{ fontSize: 14, color: color.gray700 }}>
            {full
              ? t("create.upload.full")
              : single && (files.length || selectedIds.length)
                ? t("create.upload.replaceHint")
                : t(single ? "create.upload.dropHintSingle" : "create.upload.dropHint")}
          </Typography>
        </Box>
        {limits && (
          <Typography sx={{ fontSize: 12, color: color.gray500, mt: 0.5 }}>{limits}</Typography>
        )}
        {navy && (
          <Typography sx={{ fontSize: 12, color: color.gray500, mt: 0.25 }}>
            {t("create.upload.navyHint")}
          </Typography>
        )}
      </Box>

      {rejections.length > 0 && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setRejections([])}>
          {rejections.map((message, index) => (
            <Box key={`${index}-${message}`}>{message}</Box>
          ))}
        </Alert>
      )}
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

export default UploadField;
